import { createFileRoute, Outlet, redirect } from '@tanstack/react-router'
import { PageLoader } from '@trellis/roots/loading'
import { useEffect } from 'react'

import { AnalyticsNotice } from '@/components/analytics'
import { TwoFactorEnrollmentBanner } from '@/components/two-factor'
import { env } from '@/env'
import { useAnalyticsBootstrap } from '@/hooks/useAnalyticsBootstrap'
import { authClient } from '@/lib/auth-client'
import { queryClient } from '@/lib/query-client'
import {
  clearStoredTenantDisplayName,
  clearStoredTenantSlug,
  clearStoredTenantUserId,
  getStoredTenantUserId,
  useTenant,
} from '@/lib/tenant'
import { isPastTwoFactorEnforcementDate } from '@/lib/two-factor'
import { PROTECTED_ROUTE_LOADING_MESSAGES } from '@/pages/(auth)/loading-messages'
import { TenantResolutionGuard } from '@/pages/TenantResolutionGuard'
import { signOut } from '@/requests'

type SessionData = Awaited<ReturnType<typeof authClient.getSession>>['data']

let lastResolvedSession: SessionData = null

export const Route = createFileRoute('/_authenticated')({
  beforeLoad: async ({ preload }) => {
    /*
     * Hover/intent preloads re-run beforeLoad on every hover, so reuse the last session
     * instead of hitting /api/auth/get-session each time. Real navigations always fetch
     * fresh, so sign-out and expiry are still enforced.
     */
    const session =
      preload && lastResolvedSession
        ? lastResolvedSession
        : (await authClient.getSession()).data
    lastResolvedSession = session

    if (!session) {
      throw redirect({ to: '/login' })
    }

    /*
     * The backend returns 403 EMAIL_NOT_VERIFIED on every protected API call for an
     * unverified session — sign out immediately so the user lands on /login where they can
     * request a new verification email. Storage-only tenant clear: beforeLoad runs outside
     * React, so the context-bound clearTenant() isn't reachable here; since this branch
     * always redirects away, AuthenticatedLayout's own stale-tenant effect (below) is the
     * backstop if any in-memory tenant state outlives this.
     */
    if (!session.user.emailVerified) {
      clearStoredTenantSlug()
      clearStoredTenantDisplayName()
      clearStoredTenantUserId()
      await signOut()
      queryClient.clear()
      throw redirect({ to: '/login' })
    }

    if (!session.user.twoFactorEnabled && isPastTwoFactorEnforcementDate()) {
      throw redirect({ to: '/2fa-setup' })
    }

    return { session }
  },
  pendingComponent: () => <PageLoader messages={PROTECTED_ROUTE_LOADING_MESSAGES} />,
  component: AuthenticatedLayout,
})

function AuthenticatedLayout() {
  const { session } = Route.useRouteContext()
  const { tenantSlug, clearTenant } = useTenant()
  useAnalyticsBootstrap()

  /*
   * If the stored tenant belongs to a different user than the current session, clear it so
   * TenantResolutionGuard runs for the new user. Defense-in-depth against any sign-in path
   * that doesn't go through LoginPage.
   */
  useEffect(() => {
    const storedUserId = getStoredTenantUserId()
    if (storedUserId && storedUserId !== session.user.id) {
      clearTenant()
      queryClient.clear()
    }
  }, [session.user.id, clearTenant])

  if (!tenantSlug) {
    return <TenantResolutionGuard />
  }

  return (
    <>
      {!session.user.twoFactorEnabled && env.VITE_TWO_FACTOR_ENFORCEMENT_DATE && (
        <TwoFactorEnrollmentBanner
          enforcementDate={env.VITE_TWO_FACTOR_ENFORCEMENT_DATE}
        />
      )}
      <AnalyticsNotice />
      <Outlet />
    </>
  )
}
